// The Terminal log pane's data structure and windowing math — pure, no DOM, no
// signals. `TerminalLog.tsx` owns the scroll listener and re-renders; this file
// owns what rows are held and which of them are on screen right now (#4395,
// epic #4391).
//
// FIXED-ROW-HEIGHT, HAND-ROLLED. No virtualization dependency exists in any
// `package.json`, and every row IS a fixed height by design (a log line
// scrolls sideways rather than wraps — `docs/standards/design-system.md` §
// Collapsing). That makes the windowing math a closed-form slice: given a
// scroll offset and a viewport height, the visible range is two divisions, no
// per-row measurement.

/** The minimum shape a row needs to sort into the buffer. */
export type BufferRow = { readonly id: string; readonly ts: string };

/**
 * The one row height every consumer measures against, in pixels at the
 * standard 16px root. `_terminal.scss` defines the same number as
 * `--term-row-height` in rem; `buffer.test.ts` cross-checks them so the two
 * cannot drift the way `layers.ts` / `_layers.scss` would if one were hand-kept.
 */
export const TERMINAL_ROW_HEIGHT_PX = 24;

/**
 * The buffer's default capacity. Not arbitrary: it is D-N's own search-depth
 * ceiling (`docs handoff overview.md`) — "scroll-back … until 5,000 rows have
 * been read" — reused here rather than a second number nobody reconciles.
 * A client-side number only: no server route reads or mirrors it.
 */
export const TERMINAL_BUFFER_CAPACITY = 5000;

/**
 * Whether scroll-back has hit the buffer's ceiling (plan 101 slice 19 row
 * 19-2): the buffer is full and the server still has older rows. Past this
 * point a "load earlier" page is OLDER than every row held, so the `merge()`
 * that brings it in evicts it again; offering the button would fetch a page
 * the screen never shows. The pane stops offering it and states the ceiling.
 */
export function atScrollbackCeiling(held: number, hasMore: boolean): boolean {
  return hasMore && held >= TERMINAL_BUFFER_CAPACITY;
}

/** Ascending by `(ts, id)` — the SAME pair-compare the server's keyset uses
 *  (`wire/terminal.ts`'s same-millisecond-tiebreak note), so a client-side
 *  re-sort never disagrees with the cursor that paged a batch in. Exported so
 *  a caller comparing a single row against a marker (`countAfter`, a scroll
 *  position) uses the identical tiebreak rather than a second one that could
 *  disagree with it at the boundary. */
export function compareRow(a: BufferRow, b: BufferRow): number {
  return a.ts === b.ts ? (a.id < b.id ? -1 : a.id > b.id ? 1 : 0) : a.ts < b.ts ? -1 : 1;
}

/**
 * How many of `rows` sort strictly AFTER `marker` in `(ts, id)` order.
 *
 * `rows` is assumed already ascending (every `TerminalRingBuffer.all()` is),
 * so this is one linear scan from the tail rather than a second sort — the
 * shape a "N new since I stopped following" pill and a "N lines arrived while
 * disconnected" line both need, over the SAME comparator `merge()` sorts by,
 * so a live tail's late-row reordering can never disagree with either count.
 */
export function countAfter<T extends BufferRow>(rows: readonly T[], marker: BufferRow): number {
  let count = 0;
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (row === undefined || compareRow(row, marker) <= 0) break;
    count += 1;
  }
  return count;
}

/**
 * A capped, id-deduped, chronologically-ordered buffer of rows — the ring in
 * "ring buffer": once `capacity` is exceeded, the OLDEST rows are evicted, in
 * `(ts, id)` order, never by arrival order (a scroll-back page can arrive after
 * a live one and still sort ahead of it).
 *
 * Merge, not append: `merge()` accepts a batch in ANY order (a backfill page
 * is newest-first, a live poll is whatever the server handed back) and folds
 * it into the held set by id, so a live row's late correction of an id already
 * held (there is none today, but the shape costs nothing) replaces rather than
 * duplicates.
 */
export class TerminalRingBuffer<T extends BufferRow> {
  private rows: T[] = [];

  constructor(private readonly capacity: number) {
    if (capacity <= 0) {
      throw new RangeError(`TerminalRingBuffer: capacity must be > 0, got ${capacity}`);
    }
  }

  /** Every row held, oldest first — the order the log pane renders in. */
  all(): readonly T[] {
    return this.rows;
  }

  size(): number {
    return this.rows.length;
  }

  merge(batch: readonly T[]): void {
    if (batch.length === 0) return;
    const byId = new Map(this.rows.map((r) => [r.id, r] as const));
    for (const row of batch) byId.set(row.id, row);
    const merged = [...byId.values()].sort(compareRow);
    this.rows =
      merged.length > this.capacity ? merged.slice(merged.length - this.capacity) : merged;
  }

  clear(): void {
    this.rows = [];
  }
}

/** What `computeWindow` returns: the slice of rows to actually render. */
export type WindowRange = {
  /** First index to render, inclusive. */
  readonly start: number;
  /** Last index to render, exclusive. */
  readonly end: number;
  /** `start * rowHeight` — the `translateY` (or `margin-top`) the rendered
   *  slice sits at, so it lands where its real position in the full list is. */
  readonly topOffset: number;
  /** `rowCount * rowHeight` — the scroll port's full scrollable height. */
  readonly totalHeight: number;
};

/**
 * The visible slice of a `rowCount`-row, fixed-`rowHeight` list at
 * `scrollTop`, padded by `overscan` rows on each side so a fast scroll or a
 * focus jump doesn't flash blank rows before the next paint.
 *
 * Pure arithmetic — no row is measured, because every row is the same height
 * by construction. Zero rows or a non-positive height return an all-zero
 * range rather than dividing by it.
 */
export function computeWindow(input: {
  readonly rowCount: number;
  readonly rowHeight: number;
  readonly viewportHeight: number;
  readonly scrollTop: number;
  readonly overscan?: number;
}): WindowRange {
  const { rowCount, rowHeight, viewportHeight, scrollTop } = input;
  if (rowCount <= 0 || rowHeight <= 0) {
    return { start: 0, end: 0, topOffset: 0, totalHeight: 0 };
  }
  const overscan = input.overscan ?? 6;
  const totalHeight = rowCount * rowHeight;
  const firstVisible = Math.floor(scrollTop / rowHeight);
  const visibleCount = Math.ceil(viewportHeight / rowHeight) + 1;
  const start = Math.max(0, firstVisible - overscan);
  const end = Math.min(rowCount, firstVisible + visibleCount + overscan);
  return { start, end, topOffset: start * rowHeight, totalHeight };
}
